import { useCallback } from 'react'
import type { TFunction } from 'i18next'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { db } from '../../../lib/db/indexeddb'
import { scheduleSync } from '../../../lib/sync/sync-engine'
import type { Task } from '../../../lib/db/models'
import type { TaskCreationData } from '../components/TaskEntry'
import { useSoftRemoveSound } from '@/src/renderer/lib/sounds'

interface UseTaskOperationsParams {
  t: TFunction
  /** Optional optimistic-update sink. When provided, every mutation
   *  here calls it BEFORE the IDB write so the change is visible in
   *  the next paint instead of waiting on the LiveQuery refire. */
  setOptimisticTaskOverride?: (taskId: string, partial: Partial<Task>) => void
  /** Optional in-memory `tasks` snapshot used by toggle-complete to
   *  read the current `status` synchronously (for the optimistic flip)
   *  without an extra `db.getTask` roundtrip. */
  tasks?: Task[]
}

export function useTaskOperations({ t, setOptimisticTaskOverride, tasks }: UseTaskOperationsParams) {
  const playSoftRemove = useSoftRemoveSound()

  const handleUpdateTask = useCallback(async (taskId: string, data: TaskCreationData) => {
    const updates = {
      title: data.title,
      description: data.description,
      ...(data.attention !== undefined && { attention: data.attention }),
      ...(data.estimatedDuration !== undefined && { estimatedDuration: data.estimatedDuration }),
      ...(data.durationMode !== undefined && { durationMode: data.durationMode }),
      ...(data.tags !== undefined && { tags: data.tags }),
      ...(data.startDate !== undefined && { startDate: data.startDate }),
      ...(data.startTime !== undefined && { startTime: data.startTime }),
      ...(data.startPeriod !== undefined && { startPeriod: data.startPeriod }),
      ...(data.dueDate !== undefined && { dueDate: data.dueDate }),
      ...(data.dueTime !== undefined && { dueTime: data.dueTime }),
      ...(data.duePeriod !== undefined && { duePeriod: data.duePeriod }),
    }
    setOptimisticTaskOverride?.(taskId, updates)
    await db.updateTask(taskId, updates)
    scheduleSync()
  }, [setOptimisticTaskOverride])

  const handleToggleComplete = useCallback(async (ids: Set<string>) => {
    // Decide the target state from the in-memory snapshot (which already
    // reflects any pending optimistic override). Without this, a second
    // rapid click reads the still-stale raw Dexie row via `db.getTask`
    // and flips back into the same direction as the first click — the
    // UI then visibly snaps "no change". The optimistic flip and the
    // DB write MUST use the same source of truth.
    const now = new Date()
    const idArray = [...ids]
    const byId = tasks ? new Map(tasks.map(t => [t.id, t])) : null
    for (const taskId of idArray) {
      let snapshot = byId?.get(taskId)
      if (!snapshot) {
        // Snapshot missing (rare — e.g. task only in Dexie, not in
        // page tasks). Fall back to the raw Dexie row.
        const fromDb = await db.getTask(taskId)
        if (!fromDb) continue
        snapshot = fromDb
      }
      const isCompleted = snapshot.status === 'completed'
      const next = isCompleted
        ? { status: 'todo' as const, completedAt: null }
        : { status: 'completed' as const, completedAt: now }
      setOptimisticTaskOverride?.(taskId, next)
      await db.updateTask(taskId, next)
    }
    scheduleSync()
  }, [setOptimisticTaskOverride, tasks])

  const handleDeleteTask = useCallback(async (ids: Set<string>) => {
    // Capture full Dexie records for undo (records may be hard-deleted by sync purge)
    const results = await Promise.all([...ids].map(id => db.tasks.get(id)))
    const snapshots = results.filter((s): s is NonNullable<typeof s> => s != null)
    if (snapshots.length === 0) return

    snapshots.forEach((_, i) => setTimeout(() => playSoftRemove(), i * 80))

    // Optimistic delete: mark `deletedAt` so segment builders drop the
    // task in the next paint. The actual Dexie write follows.
    const deletedAt = new Date()
    if (setOptimisticTaskOverride) {
      for (const snap of snapshots) setOptimisticTaskOverride(snap.id, { deletedAt })
    }

    for (const id of ids) await db.deleteTask(id)

    const message = snapshots.length === 1
      ? t('taskItem.deleted', { title: snapshots[0]!.title })
      : t('taskItem.deletedMultiple', { count: snapshots.length })

    showUndoToast({
      message,
      undoLabel: t('common.undo'),
      onUndo: async () => {
        // Optimistic restore: clear `deletedAt` immediately so the row
        // reappears without waiting on the LiveQuery refire.
        if (setOptimisticTaskOverride) {
          for (const snap of snapshots) setOptimisticTaskOverride(snap.id, { deletedAt: null })
        }
        for (const snap of snapshots) {
          await db.tasks.put({ ...snap, deletedAt: null, synced: false })
          await db.queueSyncChange(
            snap.cloudId ? 'update' : 'create',
            'tasks',
            { localId: snap.id, cloudId: snap.cloudId }
          )
        }
        scheduleSync()
      },
    })

    scheduleSync()
  }, [t, playSoftRemove, setOptimisticTaskOverride])

  return { handleUpdateTask, handleToggleComplete, handleDeleteTask }
}
